<template>
  <ul class="list-group list-group-flush py-3">
    <ListGroupItemPlaceholder v-if="loading === true" />
    <slot v-else />
  </ul>
</template>
<script lang="ts" setup>
import {listGroupInjectionKey} from '@/components/shared/bootstrap/BootstrapListGroup/injectionKeys';

const emits = defineEmits<{
  updateActiveListId: [id: string | number | undefined];
}>();

const props = defineProps<{
  loading?: boolean;
  defaultActiveId?: string | number;
  ids?: string[] | number[];
}>();

const activeListId = ref<string | number | undefined>(props.defaultActiveId);
const handleUpdateActive = (id: string | number) => (activeListId.value = id);

/**
 * Using a provider for @see ListGroupItem.vue nesting
 * as an alternative to Scoped Slots, avoiding Props Drilling.
 * @see {@link https://vuejs.org/guide/components/provide-inject.html}
 */
provide(listGroupInjectionKey, {
  activeListId: readonly(activeListId),
  handleUpdateActive,
});

watch(activeListId, (newId, oldId) => {
  if (newId !== oldId) emits('updateActiveListId', activeListId.value);
});

/**
 * Some lists contain removable items @see CategoryListGroup.vue
 * If the user removes an item, that is currently active, we need to reset
 * the activeListId in order to navigate away from any active views AND
 * reset Bootstrap's active item color styles
 */
const ids = computed(() => props.ids);
watch(ids, (newIds, oldIds) => {
  if (newIds !== undefined && oldIds !== undefined && newIds.length < oldIds.length) {
    const id = newIds?.find((k) => k === activeListId.value);
    if (id === undefined && props.defaultActiveId !== undefined)
      handleUpdateActive(props.defaultActiveId);
  }
});

// props.defaultActiveId can often be undefined onMount due to asynchronous requests
onBeforeUpdate(() => (activeListId.value = activeListId.value || props.defaultActiveId));
</script>
